import type { IAssetLoader } from './domLoader.js';
import type { IInlineAsset, IModuleAsset, IFileAsset } from '../../../shared/assets/index.js';

export enum ELoadbleSourceStatus {
  Initial,
  Loading,
  Loaded,
  Failed,
}

export interface ILoadableSource<Exports = void> {
  exports?: Exports;
  status: ELoadbleSourceStatus;
  load(): Promise<void>;
  emitLoading(): void;
  emitLoad(srcExports: Exports): void;
  emitError(err: Error): void;
}

abstract class LoadableSource<Exports = void> implements ILoadableSource<Exports> {
  private loadListeners: Array<EventListenerOrEventListenerObject> = [];
  private errorListeners: Array<EventListenerOrEventListenerObject> = [];
  private _status: ELoadbleSourceStatus;
  private timer: NodeJS.Timeout | number | undefined;
  private eventTarget = document.createElement('span');

  public exports?: Exports;

  public get status() {
    return this._status;
  }

  protected abstract loadSource(): Promise<Exports>;

  public async load() {
    if (this._status === ELoadbleSourceStatus.Loaded) {
      return;
    }

    if (this._status === ELoadbleSourceStatus.Loading) {
      return this.inQueue() as Promise<void>;
    }

    this.emitLoading();

    this.process();

    return this.inQueue() as Promise<void>;
  }

  public async emitLoading() {
    this._status = ELoadbleSourceStatus.Loading;
  }

  public emitLoad(srcExports: Exports) {
    if (this._status !== ELoadbleSourceStatus.Loading) {
      return;
    }

    this._status = ELoadbleSourceStatus.Loaded;

    this.exports = srcExports;

    this.cleanQueue();
  }

  public emitError(err: Error) {
    if (this._status !== ELoadbleSourceStatus.Loading) {
      return;
    }

    this._status = ELoadbleSourceStatus.Failed;

    this.cleanQueue(err);
  }

  private async process() {
    try {
      this.emitLoad(await this.loadSource());
    } catch (error) {
      this.emitError(error);
    }
  }

  private cleanQueue(err?: Error) {
    if (this.timer) {
      clearTimeout(this.timer);

      this.timer = undefined;
    }

    if (err) {
      this.eventTarget.dispatchEvent(new CustomEvent('error', { detail: err }));
    } else {
      this.eventTarget.dispatchEvent(new CustomEvent('load'));
    }

    this.loadListeners.forEach(listener => this.eventTarget.removeEventListener('load', listener));
    this.loadListeners = [];
    this.errorListeners.forEach(listener => this.eventTarget.removeEventListener('error', listener));
    this.errorListeners = [];
  }

  private inQueue() {
    return new Promise((resolve, reject) => {
      const errHandler = (err: CustomEvent<Error>) => reject(err.detail);

      this.loadListeners.push(resolve);
      this.errorListeners.push(errHandler);

      this.eventTarget.addEventListener('load', resolve);
      this.eventTarget.addEventListener('error', errHandler);
    });
  }
}

export class FileAsset<T extends IFileAsset = IFileAsset>
  extends LoadableSource
  implements IFileAsset<T['contentType']>
{
  public readonly exports = undefined;

  private readonly asset: T;
  private readonly assetLoader: IAssetLoader;
  private readonly loadTimeout?: number;

  public constructor(asset: T, assetLoader: IAssetLoader, loadTimeout?: number) {
    super();

    this.asset = asset;
    this.assetLoader = assetLoader;
    this.loadTimeout = loadTimeout;
  }

  public get type() {
    return this.asset.type;
  }

  public get contentType(): T['contentType'] {
    return this.asset.contentType;
  }

  public get src() {
    return this.asset.src;
  }

  public get props() {
    return this.asset.props;
  }

  public loadSource(): Promise<void> {
    return this.assetLoader.import(this, this.loadTimeout);
  }
}

export class ModuleAsset<Exports = void> extends LoadableSource<Exports> implements IModuleAsset {
  private readonly asset: IModuleAsset;
  private readonly assetLoader: IAssetLoader;
  private readonly loadTimeout?: number;

  public constructor(asset: IModuleAsset, assetLoader: IAssetLoader, loadTimeout?: number) {
    super();

    this.asset = asset;
    this.assetLoader = assetLoader;
    this.loadTimeout = loadTimeout;
  }

  public get type() {
    return this.asset.type;
  }

  public get contentType() {
    return this.asset.contentType;
  }

  public get src() {
    return this.asset.src;
  }

  public get props() {
    return { ...this.asset.props, 'data-moduletype': 'microfrontend' };
  }

  public get hasExports() {
    return this.asset.hasExports;
  }

  public async loadSource(): Promise<Exports> {
    return this.assetLoader.import<Exports>(this, this.loadTimeout);
  }
}

export class InlineAsset implements IInlineAsset {
  private readonly asset: IInlineAsset;
  private readonly assetLoader: IAssetLoader;

  public constructor(asset: IInlineAsset, assetLoader: IAssetLoader) {
    this.asset = asset;
    this.assetLoader = assetLoader;
  }

  public get type() {
    return this.asset.type;
  }

  public get contentType() {
    return this.asset.contentType;
  }

  public get content() {
    return this.asset.content;
  }

  public get hash() {
    return this.asset.hash;
  }

  public load() {
    this.assetLoader.importInline(this);
  }
}
